import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/github/workspace/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const InlineAlert = makeShortcode("InlineAlert");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "lifecycle-behavior-reference"
    }}>{`Lifecycle behavior reference`}</h1>
    <p>{`The Lifecycle extension relies solely on the Lifecycle events dispatched by the `}<a parentName="p" {...{
        "href": "api-reference.md#lifecyclestart"
      }}><inlineCode parentName="a">{`lifecycleStart`}</inlineCode></a>{` and `}<a parentName="p" {...{
        "href": "api-reference.md#lifecyclepause"
      }}><inlineCode parentName="a">{`lifecyclePause`}</inlineCode></a>{` calls to determine the start of new sessions and to calculate lifecycle data.`}</p>
    <h2 {...{
      "id": "general-behavior"
    }}>{`General behavior`}</h2>
    <p>{`A Lifecycle session is the time between a `}<inlineCode parentName="p">{`lifecycleStart`}</inlineCode>{` call and a `}<inlineCode parentName="p">{`lifecyclePause`}</inlineCode>{` call. A new session is detected when `}<inlineCode parentName="p">{`lifecycleStart`}</inlineCode>{` is called after a `}<inlineCode parentName="p">{`lifecyclePause`}</inlineCode>{` and the `}<a parentName="p" {...{
        "href": "configuration-keys.md#lifecyclesessiontimeout"
      }}>{`Lifecycle session timeout`}</a>{` has elapsed.`}</p>
    <p>{`All events that are not Lifecycle start or Lifecycle pause do not affect Lifecycle state or data. Starting MobileCore using `}<inlineCode parentName="p">{`registerExtensions`}</inlineCode>{` does not automatically start a Lifecycle session nor does it affect Lifecycle data.`}</p>
    <h3 {...{
      "id": "consecutive-lifecyclestart-api-calls"
    }}>{`Consecutive `}<inlineCode parentName="h3">{`lifecycleStart`}</inlineCode>{` API calls`}</h3>
    <p>{`Consecutive `}<inlineCode parentName="p">{`lifecycleStart`}</inlineCode>{` calls will not update the session start timestamp from the initial `}<inlineCode parentName="p">{`lifecycleStart`}</inlineCode>{` call, and Lifecycle data is not changed.`}</p>
    <InlineAlert variant="info" slots="text" mdxType="InlineAlert" />
    <p>{`In this case, a Lifecycle shared state is dispatched with its data reflecting the start timestamp from the initial `}<inlineCode parentName="p">{`lifecycleStart`}</inlineCode>{` call. This can be verified in both application logs or Assurance.`}</p>
    <h3 {...{
      "id": "consecutive-lifecyclepause-api-calls"
    }}>{`Consecutive `}<inlineCode parentName="h3">{`lifecyclePause`}</inlineCode>{` API calls`}</h3>
    <p>{`Consecutive `}<inlineCode parentName="p">{`lifecyclePause`}</inlineCode>{` calls will update the persisted pause timestamp. On the next `}<inlineCode parentName="p">{`lifecycleStart`}</inlineCode>{` API call, the last pause timestamp is used to determine if the previous session has timed out and a new session is started.`}</p>
    <h2 {...{
      "id": "expected-lifecycle-scenarios"
    }}>{`Expected Lifecycle scenarios`}</h2>
    <p>{`The following Lifecycle workflows show examples of the expected ordering of Lifecycle events.`}</p>
    <p><strong parentName="p">{`App first launch after installation`}</strong></p>
    <p><img parentName="p" {...{
        "src": "/client-sdks/assets/289701e75e24bf8f23329456274e091b/lifecycle-first-launch.svg",
        "alt": null
      }}></img></p>
    {/* mermaid.js diagram definition
     graph LR
        A(["1.<br/>App launched"])
        A ==> B("2.<br/><code>lifecycleStart</code><br>(Start of new session)")
        B ==> C(3.<br/><code>lifecyclePause</code>)
        C ==> D(["4.<br/>App closed by user"])
         classDef dashedPill fill:#d3d3d3,stroke:#000,stroke-dasharray: 5 5,color:#000;
        classDef regularPill fill:#3273de,stroke:#3273de,color:#fff;
        classDef regularBox fill:#009c3b,stroke:#009c3b,color:#fff;
        classDef incorrectBox fill:#EB1000,stroke:#EB1000,color:#fff;
         class A,D regularPill;
        class B,C regularBox;
     */}
    <p><strong parentName="p">{`Subsequent app launches, continue session with app close`}</strong></p>
    <p><img parentName="p" {...{
        "src": "/client-sdks/assets/349093fc619da0ca20bf6f9f593ea4c7/lifecycle-continue-session.svg",
        "alt": null
      }}></img></p>
    {/* mermaid.js diagram definition
     graph LR
        A(["1.<br/>App launched"])
        A ==> B("2.<br/><code>lifecycleStart</code>")
        B ==> C(3.<br/><code>lifecyclePause</code>)
        C ==> D(["4.<br/>App closed by user"])
        D ==> E(["5.<br/>App launched<br>(Session timeout window<br>did <b>not</b> pass)"])
        E ==> F("6.<br/><code>lifecycleStart</code><br>(Continue current session)")
         classDef dashedPill fill:#d3d3d3,stroke:#000,stroke-dasharray: 5 5,color:#000;
        classDef regularPill fill:#3273de,stroke:#3273de,color:#fff;
        classDef regularBox fill:#009c3b,stroke:#009c3b,color:#fff;
        classDef incorrectBox fill:#EB1000,stroke:#EB1000,color:#fff;
         class A,D,E regularPill;
        class B,C,F regularBox;
     */}
    <p><strong parentName="p">{`Subsequent app launches, continue session without app close`}</strong></p>
    <p><img parentName="p" {...{
        "src": "/client-sdks/assets/0f46312b4ff0a5d0c31c7704cbe61d76/lifecycle-continue-without-close.svg",
        "alt": null
      }}></img></p>
    {/* mermaid.js diagram definition
     graph LR
        A(["1.<br/>App launched"])
        A ==> B("2.<br/><code>lifecycleStart</code>")
        B ==> C(3.<br/><code>lifecyclePause</code>)
        C ==> D("4.<br/><code>lifecycleStart</code><br>(Continue current session)")
        D ==> E(5.<br/><code>lifecyclePause</code>)
        E ==> F(["6.<br/>App closed by user"])
        subgraph "Can repeat any number of times"
            E ==> D
        end
         classDef dashedPill fill:#d3d3d3,stroke:#000,stroke-dasharray: 5 5,color:#000;
        classDef regularPill fill:#3273de,stroke:#3273de,color:#fff;
        classDef regularBox fill:#009c3b,stroke:#009c3b,color:#fff;
        classDef incorrectBox fill:#EB1000,stroke:#EB1000,color:#fff;
         class A,F regularPill;
        class B,C,D,E regularBox;
     */}
    <p><strong parentName="p">{`Subsequent app launches, new session`}</strong></p>
    <p><img parentName="p" {...{
        "src": "/client-sdks/assets/10561c10df8750016d2d22e5c8d4b413/lifecycle-subsequent-new-session.svg",
        "alt": null
      }}></img></p>
    {/* mermaid.js diagram definition
     graph LR
        A(["1.<br/>App launched"])
        A ==> B("2.<br/><code>lifecycleStart</code>")
        B ==> C("3.<br/><code>lifecyclePause</code>")
        C ==> D(["4.<br/>App closed by user"])
        D ==> E(["5.<br/>Session timeout<br>window passed"])
        E ==> F(["6.<br/>App launched"])
        F ==> G("7.<br/><code>lifecycleStart</code><br/>(Start of new session)")
         classDef dashedPill fill:#d3d3d3,stroke:#000,stroke-dasharray: 5 5,color:#000;
        classDef regularPill fill:#3273de,stroke:#3273de,color:#fff;
        classDef regularBox fill:#009c3b,stroke:#009c3b,color:#fff;
        classDef incorrectBox fill:#EB1000,stroke:#EB1000,color:#fff;
         class A,,D,E,F regularPill;
        class B,C,G regularBox;
     */}
    <p><strong parentName="p">{`On app crash or force close`}</strong></p>
    <p><img parentName="p" {...{
        "src": "/client-sdks/assets/c6c132b125aa3d4dbb5f2a84864d8ded/lifecycle-crash.svg",
        "alt": null
      }}></img></p>
    {/* mermaid.js diagram definition
     graph LR
        A(["1.<br/>App launched"])
        A ==> B(2.<br/><code>lifecycleStart</code>)
        B ==> C(["3.<br/>App crashed/force closed<br>(Unable to call <code>lifecyclePause</code>)"])
        C ==> D(["4.<br/>App launched"])
        D ==> E("5.<br/><code>lifecycleStart</code><br>(Crash reported)")
         classDef dashedPill fill:#d3d3d3,stroke:#000,stroke-dasharray: 5 5,color:#000;
        classDef regularPill fill:#3273de,stroke:#3273de,color:#fff;
        classDef regularBox fill:#009c3b,stroke:#009c3b,color:#fff;
        classDef incorrectBox fill:#EB1000,stroke:#EB1000,color:#fff;
         class A,C,D regularPill;
        class B,E regularBox;
     */}
    <h2 {...{
      "id": "troubleshooting-unexpected-lifecycle-scenarios"
    }}>{`Troubleshooting unexpected Lifecycle scenarios`}</h2>
    <p>{`The following Lifecycle workflows show examples of the unexpected ordering of Lifecycle events that can impact Lifecycle data and can indicate an incorrect implementation of the Lifecycle APIs. To address the following scenarios, refer to the `}<a parentName="p" {...{
        "href": "/client-sdks/src/pages/home/base/mobile-core/lifecycle/index.md"
      }}>{`implementation guide for Lifecycle`}</a>{`.`}</p>
    <p><strong parentName="p">{`Missing pause, app terminated`}</strong>{`  `}</p>
    <p>{`This scenario looks the same as a standard crash, but the underlying reason is an incorrect implementation where `}<inlineCode parentName="p">{`lifecyclePause`}</inlineCode>{` is not called before the app is terminated.`}</p>
    <p><img parentName="p" {...{
        "src": "/client-sdks/assets/cf29e19197969e8936c7ed65deff2298/lifecycle-missing-pause-terminated.svg",
        "alt": null
      }}></img></p>
    {/* mermaid.js diagram definition
     graph LR
        A(["1.<br/>App launched"])
        A ==> B("2.<br/><code>lifecycleStart</code>")
        B ==> C(["3.<br/>App closed by user<br>(missing <code>lifecyclePause</code> call)"])
        C ==> D(["4.<br/>App terminated"])
        D ==> E("5.<br/><code>lifecycleStart</code><br>(Crash reported)")
         classDef dashedPill fill:#d3d3d3,stroke:#000,stroke-dasharray: 5 5,color:#000;
        classDef regularPill fill:#3273de,stroke:#3273de,color:#fff;
        classDef regularBox fill:#009c3b,stroke:#009c3b,color:#fff;
        classDef incorrectBox fill:#EB1000,stroke:#EB1000,color:#fff;
         class A,D regularPill;
        class B,E regularBox;
        class C incorrectBox;
     */}
    <p><strong parentName="p">{`Missing pause, app still in memory`}</strong>{`  `}</p>
    <p>{`This scenario shows an example of `}<a parentName="p" {...{
        "href": "#consecutive-lifecyclestart-api-calls"
      }}>{`consecutive `}<inlineCode parentName="a">{`lifecycleStart`}</inlineCode>{` API calls`}</a>{`. The app close is not detected because `}<inlineCode parentName="p">{`lifecyclePause`}</inlineCode>{` is not called. As the app is not removed from memory, the current session continues.`}</p>
    <p><img parentName="p" {...{
        "src": "/client-sdks/assets/011c88132f5fcbaaca63442f696902b3/lifecycle-missing-pause-not-terminated.svg",
        "alt": null
      }}></img></p>
    {/* mermaid.js diagram definition
     graph LR
        A(["1.<br/><code>App launched</code>"])
        A ==> B("2.<br/><code>lifecycleStart</code>")
        B ==> C(["3.<br/>App closed by user<br>(Missing <code>lifecyclePause</code> call)"])
        C ==> D(["4.<br/><code>App launched</code>"])
        D ==> E("5.<br/><code>lifecycleStart</code><br>(Session continues)")
         classDef dashedPill fill:#d3d3d3,stroke:#000,stroke-dasharray: 5 5,color:#000;
        classDef regularPill fill:#3273de,stroke:#3273de,color:#fff;
        classDef regularBox fill:#009c3b,stroke:#009c3b,color:#fff;
        classDef incorrectBox fill:#EB1000,stroke:#EB1000,color:#fff;
         class A,D,E regularPill;
        class B,E regularBox;
        class C incorrectBox;
     */}
    <p>{`In this scenario, the `}<inlineCode parentName="p">{`lifecycleStart`}</inlineCode>{` call (5) is not detected as a new session nor a crash. It will have the consequences of a `}<a parentName="p" {...{
        "href": "#consecutive-lifecyclestart-api-calls"
      }}>{`consecutive `}<inlineCode parentName="a">{`lifecycleStart`}</inlineCode>{` API call`}</a>{`.`}</p>
    <p><strong parentName="p">{`Missing start`}</strong>{`  `}</p>
    <p>{`This scenario shows an example of `}<a parentName="p" {...{
        "href": "#consecutive-lifecyclepause-api-calls"
      }}>{`consecutive `}<inlineCode parentName="a">{`lifecyclePause`}</inlineCode>{` API calls`}</a>{`. The new session is not detected because `}<inlineCode parentName="p">{`lifecycleStart`}</inlineCode>{` is not called.`}</p>
    <p><img parentName="p" {...{
        "src": "/client-sdks/assets/554eb7023f12d9f4ff01b9e778c0aa97/lifecycle-missing-start.svg",
        "alt": null
      }}></img></p>
    {/* mermaid.js diagram definition
     graph LR
        A(["1.<br/><code>App launched</code>"])
        A ==> B("2.<br/><code>lifecycleStart</code>")
        B ==> C("3.<br/><code>lifecyclePause</code>")
        C ==> D(["4.<br/>App closed by user"])
        D ==> E(["5.<br/>(Optionally)<br>Session timeout window passed"])
        E ==> F(["6.<br/><code>App launched</code>"])
        F ==> G("7.<br/><code>lifecyclePause</code><br>(Missing <code>lifecycleStart</code> call)")
         classDef dashedPill fill:#d3d3d3,stroke:#000,stroke-dasharray: 5 5,color:#000;
        classDef regularPill fill:#3273de,stroke:#3273de,color:#fff;
        classDef regularBox fill:#009c3b,stroke:#009c3b,color:#fff;
        classDef incorrectBox fill:#EB1000,stroke:#EB1000,color:#fff;
         class A,D,F regularPill;
        class B,C regularBox;
        class E dashedPill;
        class G incorrectBox;
     */}
    <p>{`In this scenario, the last `}<inlineCode parentName="p">{`lifecyclePause`}</inlineCode>{` call (7) is effectively a `}<a parentName="p" {...{
        "href": "#consecutive-lifecyclepause-api-calls"
      }}>{`consecutive `}<inlineCode parentName="a">{`lifecyclePause`}</inlineCode>{` API call`}</a>{`, with the consequences of:`}</p>
    <ol>
      <li parentName="ol">{`Actual app background time - the time between `}<inlineCode parentName="li">{`lifecyclePause`}</inlineCode>{` calls (3) and (7) - to be included in Lifecycle data calculations.`}</li>
      <li parentName="ol">{`Potentially affecting detection of a new session start.`}</li>
    </ol>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      